import { FormControl, Grid, InputLabel, MenuItem, Select } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { selectAuthorizedFacilities } from 'store/selectors/facilitySelectors';
import { getStateDisplayName } from 'store/utils/shipmentUtils';
import { InventoryAlertProvider } from './context/InventoryAlertContext';
import InventoryCountBanner from './components/InventoryCountBanner';
import InventoryAlertDialog from './components/InventoryAlertDialog';
import InventoryDashboardFacilityTable from './components/InventoryDashboardFacilityTable';
import InventoryDashboardTable from './components/InventoryDashboardTable';
import MetricCards from './components/MetricCards';

const ALL_FACILITIES = 'ALL';
const FACILITY_STORAGE_KEY = 'inventoryDashboard.selectedFacility';

const InventoryDashboard = () => {
  const gridRef = useRef(null);
  const [storedFacility, setStoredFacility] = useState(
    () => sessionStorage.getItem(FACILITY_STORAGE_KEY) ?? ALL_FACILITIES
  );

  // The user's own facilities, not the tenant's. This read used to be
  // `pharmacy[currentTenantId]`, which pharmacyThunks fills with every active facility in the
  // tenant — so the picker offered facilities the user had no access to (POM-6274).
  const facilities = useSelector(selectAuthorizedFacilities);

  /*
   * The stored choice is only as good as the access behind it: it survives a session in which
   * that access changed, and it is editable. Anything not in the list falls back to All
   * Facilities rather than being sent as a filter.
   */
  const selectedFacility = useMemo(
    () =>
      storedFacility === ALL_FACILITIES ||
      facilities.some((p) => p.fulfillingPharmacyId === storedFacility)
        ? storedFacility
        : ALL_FACILITIES,
    [storedFacility, facilities]
  );

  const bannerPharmacyIds = useMemo(
    () =>
      selectedFacility === ALL_FACILITIES
        ? facilities.map((p) => p.fulfillingPharmacyId)
        : [selectedFacility],
    [selectedFacility, facilities]
  );

  useEffect(() => {
    document.title = 'Inventory Dashboard - SmartOS';
  }, []);

  const handleFacilityChange = (event) => {
    const { value } = event.target;
    setStoredFacility(value);
    sessionStorage.setItem(FACILITY_STORAGE_KEY, value);
  };

  return (
    <InventoryAlertProvider>
      <Grid container sx={{ px: 3, py: 3 }} direction='column' spacing={2}>
        <Grid item container justifyContent='space-between' alignItems='center'>
          <MetricCards facilityId={selectedFacility} />
          <FormControl size='small' sx={{ minWidth: 260 }}>
            <InputLabel id='inventory-dashboard-facility-label'>Facility</InputLabel>
            <Select
              labelId='inventory-dashboard-facility-label'
              id='inventory-dashboard-facility-select'
              label='Facility'
              value={selectedFacility}
              onChange={handleFacilityChange}
            >
              <MenuItem value={ALL_FACILITIES}>All Facilities</MenuItem>
              {facilities.map((p) => (
                <MenuItem key={p.fulfillingPharmacyId} value={p.fulfillingPharmacyId}>
                  {p.state ? `${p.name} (${getStateDisplayName(p.state)})` : p.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item container>
          <InventoryCountBanner fulfillingPharmacyIds={bannerPharmacyIds} />
        </Grid>

        <Grid item container>
          {selectedFacility === ALL_FACILITIES ? (
            <InventoryDashboardTable gridRef={gridRef} />
          ) : (
            /*
             * Keyed on the facility. Without it the table stayed mounted through a switch and
             * left the previous facility's rows under a progress bar, beside metric cards that
             * had already blanked. A refresh still keeps it mounted — manual adjustment, physical
             * count and alert reloads do not change the facility — so the column state POM-6140
             * set out to protect survives those exactly as before.
             */
            <InventoryDashboardFacilityTable key={selectedFacility} facilityId={selectedFacility} />
          )}
        </Grid>
      </Grid>

      <InventoryAlertDialog />
    </InventoryAlertProvider>
  );
};

export default InventoryDashboard;
